<script setup>
import { RouterLink } from 'vue-router'
import footer from '@/content/footer.yml'
import { withTrailingSlash } from '@/utils/siteUrl'

const year = new Date().getFullYear()
</script>

<template>
  <footer>
    <div class="inner">
      <div class="top">
        <div class="brand">
          <RouterLink class="logo" to="/" aria-label="Sylvia Rodger Institute — Home">
            <img class="logo-light" src="/logos/Logo_ForLightBG_Colour_RGB.svg" alt="Sylvia Rodger Institute" />
            <img class="logo-dark" src="/logos/Logo_ForDarkBG_Colour_RGB.svg" alt="Sylvia Rodger Institute" />
          </RouterLink>
          <p class="mission">{{ footer.mission }}</p>
          <p class="mission trading">
            {{ footer.tradingName }} <span class="entity">{{ footer.tradingEntity }}</span>
          </p>

          <div class="social">
            <p class="heading">{{ footer.social.heading }}</p>
            <ul class="links">
              <li v-for="link in footer.social.links" :key="link.label">
                <a class="link" :href="link.href" target="_blank" rel="noopener noreferrer">
                  {{ link.label }} <span class="external" aria-hidden="true">↗</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div class="sitemap">
          <nav class="columns" aria-label="Site map">
            <div v-for="col in footer.columns" :key="col.heading" class="column">
              <component :is="col.to ? RouterLink : 'p'" :to="withTrailingSlash(col.to)" class="heading">
                {{ col.heading }}
              </component>
              <ul class="links">
                <li v-for="link in col.links" :key="link.label">
                  <a
                    v-if="link.href"
                    class="link"
                    :href="link.href"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ link.label }} <span class="external" aria-hidden="true">↗</span>
                  </a>
                  <RouterLink v-else class="link" :to="withTrailingSlash(link.to)">{{ link.label }}</RouterLink>
                </li>
              </ul>
            </div>
          </nav>
        </div>
      </div>

      <div class="bottom">
        <p class="acknowledgement">{{ footer.acknowledgement }}</p>

        <div class="row">
          <p class="copyright">Copyright &copy; {{ year }} Sylvia Rodger Institute. All rights reserved. ABN: 55 162 632 180</p>
          <ul class="utility">
            <li v-for="link in footer.utility" :key="link.label">
              <a
                v-if="link.href"
                class="link"
                :href="link.href"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ link.label }} <span class="external" aria-hidden="true">↗</span>
              </a>
              <RouterLink v-else class="link" :to="withTrailingSlash(link.to)">{{ link.label }}</RouterLink>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </footer>
</template>

<style lang="scss" scoped>
footer {
  margin-top: auto;                 // pins footer to viewport bottom (App is a flex column)
  background: var(--color-bg-alt);  // Figma aut-cream2 #F5E9E0 — darker cream band

  > .inner {
    max-width: $container;
    margin: 0 auto;
    padding: $space-12 $gutter;     // Figma 48px block padding
  }

  .top {
    display: flex;
    flex-direction: column;
    gap: $space-8;

    @include from-md {
      flex-direction: row;
      justify-content: space-between;
      gap: $space-10;
    }
  }

  .brand {
    @include from-md {
      flex: 1 1 300px;
      max-width: 30rem;
    }

    .logo {
      display: inline-flex;
      text-decoration: none;

      img {
        height: 72px;
        width: auto;
        max-width: 100%;
        @include from-md { height: 88px; }
      }

      // Theme-driven swap (same rule as _tokens.scss's dark-theme-tokens
      // and Header.vue's logo) — no JS needed, System mode via media query.
      .logo-dark { display: none; }

      &:focus-visible { @include focus-ring; border-radius: $radius-sm; }
    }

    .mission {
      margin: $space-6 0 0;
      max-width: 36rem;
      font-size: $font-size-md;
      font-weight: 600;
      line-height: 1.3;
      color: var(--color-ink);
      text-wrap: pretty;

      // Trading-name sentence sits under the descriptor as its own paragraph:
      // same size/weight, a paragraph gap rather than the $space-6 section gap
      // that separates the descriptor from the logo above it.
      &.trading {
        margin-top: $space-4;

        // The legal entity should read as one unit and, at the widths where the
        // sentence doesn't fit on one line, start the second line rather than
        // splitting as "…name of Autism / CRC Ltd.". `nowrap` keeps it whole;
        // overriding the inherited `text-wrap: pretty` stops the balancer
        // pulling "of" down with it. Soft, not a `<br>` — at narrow widths the
        // sentence still reflows naturally, the entity just never breaks.
        text-wrap: wrap;

        .entity { white-space: nowrap; }
      }
    }

    .social {
      margin-top: $space-6;

      .links {
        flex-direction: row;
        flex-wrap: wrap;
        gap: $space-3 $space-5;
      }
    }
  }

  // Second .top column: just the site-links nav (Acknowledgement of Country
  // moved to .bottom, full-width, per user request).
  .sitemap {
    @include from-md {
      flex: 2 1 420px;
    }
  }

  .columns {
    display: grid;
    // jk-37 blanket rule: single column below $bp-xs (500px) — was a bare
    // 2-up grid at every width, cramming this footer's 4 nav columns into
    // 2-per-row even on the narrowest phones.
    grid-template-columns: 1fr;
    gap: $space-6 $space-5;

    @include from-xs { grid-template-columns: repeat(2, minmax(0, 1fr)); }

    // jk-37: was from-xl (1280px) — that token now means 1200px, and this
    // 4-column footer treatment was tuned for the wider "very wide desktop"
    // slot, so it moved to from-xxl (1400px) to keep the same behaviour.
    @include from-xxl {
      // Equal-WIDTH columns (not content-width) per user request — every column
      // is exactly 1/4 of the row regardless of heading/link length.
      grid-template-columns: repeat(4, minmax(0, 1fr));
      column-gap: $space-8;
      row-gap: $space-6;
    }
  }

  .column .heading {
    display: inline-block;
    margin: 0 0 $space-4;
    font-size: $font-size-sm;
    font-weight: 600;
    color: var(--color-ink);
    text-decoration: none;
  }

  // Follow Us heading + links sized up from the nav-column treatment they used
  // to share, per user request — kept as its own rule rather than a shared one.
  .social .heading {
    display: inline-block;
    margin: 0 0 $space-4;
    font-size: $font-size-base;
    font-weight: 600;
    color: var(--color-ink);
    text-decoration: none;
  }

  .social .link {
    font-size: $font-size-base;
  }

  // A linked column heading (RouterLink) gains the same hover/focus affordances.
  a.heading {
    &:hover         { text-decoration: underline; }
    &:focus-visible { @include focus-ring; border-radius: $radius-sm; }
  }

  .links {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: $space-3;

    .external { font-size: $font-size-cap; }
  }

  .bottom {
    display: flex;
    flex-direction: column;
    gap: $space-6;
    margin-top: $space-8;
    padding-top: $space-6;
    border-top: 1px solid var(--color-border);
    font-size: $font-size-cap;
    color: var(--color-text-muted);

    .acknowledgement {
      margin: 0;
      font-size: $font-size-base;
      font-weight: 400;
      line-height: 1.3;
      color: var(--color-ink);
      text-wrap: balance;
    }

    .row {
      display: flex;
      flex-direction: column;
      gap: $space-4;

      @include from-md {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
      }
    }

    .copyright { margin: 0; }

    .utility {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-wrap: wrap;
      gap: $space-3 $space-5;
    }
  }

  // Shared link treatment — inline nav/utility links carry the four states that
  // apply to a link (hover / focus / active / visited); disabled + loading are
  // button-only and have no meaning here.
  .link {
    font-size: $font-size-sm;
    color: var(--color-text-muted);
    text-decoration: none;
    @include transition(color);

    &:hover         { color: var(--color-ink); text-decoration: underline; }
    &:focus-visible { @include focus-ring; border-radius: $radius-sm; }
    &:active        { color: var(--color-ink); }
    &:visited       { color: var(--color-text-muted); }
  }

  .bottom .link { font-size: $font-size-cap; }
}

// Top-level (not nested under `footer`) — [data-theme='dark'] lives on
// <html>, an ancestor of footer, not a descendant, so this can't be written
// inside the `footer { ... }` block above.
[data-theme='dark'] footer .brand .logo .logo-light { display: none; }
[data-theme='dark'] footer .brand .logo .logo-dark  { display: block; }

// PARKED FOR LAUNCH (2026-08-05) alongside _tokens.scss's equivalent block
// — see docs/features/jk-9-dark-mode.md's "Parked for launch" follow-up.
// Without this, the OS dark-mode setting alone swapped the logo even with
// the header toggle and _tokens.scss's media query both gone.
// @media (prefers-color-scheme: dark) {
//   :root:not([data-theme='light']):not([data-theme='dark']) {
//     footer .brand .logo .logo-light { display: none; }
//     footer .brand .logo .logo-dark  { display: block; }
//   }
// }
</style>
